import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ABOUT_IMG = '/images/about-summary-img.jpg';

export default function AboutSummary() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const stats = [
    { numKey: 'about.stat1.number', labelKey: 'about.stat1.label', icon: 'ri-earth-line' },
    { numKey: 'about.stat2.number', labelKey: 'about.stat2.label', icon: 'ri-building-2-line' },
    { numKey: 'about.stat3.number', labelKey: 'about.stat3.label', icon: 'ri-calendar-event-line' },
    { numKey: 'about.stat4.number', labelKey: 'about.stat4.label', icon: 'ri-user-star-line' },
  ];

  return (
    <section id="about" className="py-20 md:py-28 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center transition-all duration-800 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Text Content */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2C7DA0]/10 text-[#2C7DA0] text-xs font-semibold mb-5 tracking-widest uppercase">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-information-line" />
              </div>
              {t('about.badge')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-5 leading-tight">
              {t('about.title')}
            </h2>
            <p className="text-[#0A2540]/60 text-base leading-relaxed mb-8">
              {t('about.description')}
            </p>
            <a
              href="/#about"
              className="inline-flex items-center gap-2 text-[#2C7DA0] font-medium text-sm hover:gap-3 transition-all cursor-pointer"
            >
              {t('about.learnMore')}
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </a>
          </div>

          {/* Image */}
          <div className="relative">
            <div className="w-full h-72 md:h-96 rounded-2xl overflow-hidden">
              <img
                src={ABOUT_IMG}
                alt="GTRA International Conference"
                className="w-full h-full object-cover object-top"
              />
            </div>
            {/* Decorative accent */}
            <div className="absolute -bottom-4 -right-4 w-2/3 h-2/3 rounded-2xl bg-[#2C7DA0]/10 -z-10" />
          </div>
        </div>

        {/* Stats Grid */}
        <div className={`grid grid-cols-2 lg:grid-cols-4 gap-4 mt-16 transition-all duration-800 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {stats.map(({ numKey, labelKey, icon }) => (
            <div
              key={numKey}
              className="bg-[#F7F9FC] rounded-xl p-6 text-center hover:bg-[#2C7DA0]/5 transition-colors duration-200"
            >
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-[#2C7DA0]/10">
                <i className={`${icon} text-[#2C7DA0] text-xl`} />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-1">
                {t(numKey)}
              </div>
              <div className="text-[#0A2540]/50 text-xs tracking-widest uppercase">
                {t(labelKey)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
